import {
  assertSameInvoice,
  submitInvoice,
  type PendingInvoice,
} from "@/services/reviews/invoiceRequest";
import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import { v4 as uuid } from "uuid";
import type { CreateCustomerPurchaseCommand } from "@/models/club";
import { useAuth } from "@/context/auth";
import { createFactor } from "@/services/payment";
import { reviews } from "@/services/reviews";
import {
  blockerLabel,
  reviewError,
  sendLabels,
  type ReviewApiError,
} from "@/services/reviews/policy";

export function useCreateReviewedFactor() {
  const { getUserByToken } = useAuth();
  const scope = `pending-invoice:${import.meta.env.VITE_API_BASE_URL}:${getUserByToken()?.phoneNumber ?? ""}`;
  const [initialPending] = useState<PendingInvoice | undefined>(() => {
    try {
      const saved = sessionStorage.getItem(scope);
      const parsed = saved ? (JSON.parse(saved) as PendingInvoice) : undefined;
      return parsed?.key && parsed.data && typeof parsed.data === "object"
        ? parsed
        : undefined;
    } catch {
      return undefined;
    }
  });
  const [pendingRecovery, setPendingRecovery] = useState(!!initialPending);
  const record = useRef<PendingInvoice | undefined>(initialPending);
  const client = useQueryClient();
  const clear = () => {
    record.current = undefined;
    try {
      sessionStorage.removeItem(scope);
    } catch {
      /* Storage may be unavailable. */
    }
    setPendingRecovery(false);
  };
  const mutation = useMutation({
    mutationFn: async (data: CreateCustomerPurchaseCommand) => {
      if (record.current) assertSameInvoice(record.current, data);
      record.current ??= { key: uuid(), data };
      try {
        sessionStorage.setItem(scope, JSON.stringify(record.current));
      } catch {
        /* Keep the in-memory key for retries. */
      }
      return submitInvoice(record.current, pendingRecovery, {
        status: reviews.request,
        create: createFactor,
      });
    },
    onSuccess: (result) => {
      if (pendingRecovery) notifications.show({ color: "green", message: `فاکتور #${result.purchaseId} ثبت شده است؛ نتیجه بازیابی شد.` });
      clear();
      void client.invalidateQueries({ queryKey: ["invoices"] });
      void client.invalidateQueries({ queryKey: ["getAppointmentDeposit"] });
      void client.invalidateQueries({ queryKey: ["getAppointmentDepositById"] });
      void client.invalidateQueries({ queryKey: ["getDailyAppointment"] });
      if (result.reviewInvitation?.requested) {
        const invitation = result.reviewInvitation;
        notifications.show({
          title: `فاکتور #${result.purchaseId} ثبت شد`,
          message: invitation.failureCode
            ? `پیامک نظرخواهی: ${blockerLabel(invitation.failureCode)}`
            : `پیامک نظرخواهی: ${sendLabels[invitation.sendStatus ?? ""] || "وضعیت نامشخص"}`,
          color: invitation.failureCode ? "yellow" : "blue",
          autoClose: 10000,
        });
      }
      if (result.paymentLinkFailureMessage)
        notifications.show({
          title: "فاکتور ثبت شد؛ ارسال لینک پرداخت ناموفق بود",
          message: result.paymentLinkFailureMessage,
          color: "yellow",
          autoClose: false,
        });
    },
    onError: (error) => {
      const status = (error as ReviewApiError).response?.status;
      if (
        status &&
        status >= 400 &&
        status < 500 &&
        status !== 408 &&
        (error as ReviewApiError).response?.data?.code !==
          "IDEMPOTENCY_CONFLICT"
      )
        clear();
      else setPendingRecovery(true);
      notifications.show({ color: "red", message: reviewError(error) });
    },
  });
  return {
    ...mutation,
    pendingRecovery,
    recover: () =>
      record.current
        ? mutation.mutateAsync(record.current.data)
        : Promise.reject(new Error("درخواست یافت نشد")),
  };
}
